import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconCalendar, IconChevronUp, IconPlayerPlay, IconRefresh, IconTrash, IconX } from '@tabler/icons-react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  BoardToolbarAreaClickUpChip,
  BoardToolbarClienteClickUpChip,
  BoardToolbarCoordenadorClickUpChip,
  BoardToolbarExpandSearch,
  BoardToolbarTecnicosClickUpChip,
} from '../../board/toolbar';
import { useBoardTechnicianAvatars } from '../../board/hooks/useBoardTechnicianAvatars';
import { EcClickUpFilterChip } from '../../experiencia-cliente/components/EcClickUpFilterChip';
import {
  PeriodModal,
  getCalendarBounds,
  toYmd,
} from '../../equipment-consult/components/EquipmentConsultProductionPeriodPicker';
import { ymdToBr } from '../../relatorios/utils/relatoriosPeriodDateUtils';
import { ROADMAP_SCHEDULE_ROUTE } from '../routes';
import {
  fetchRoadmapScheduleView,
  fetchTeamForScheduleFilters,
  fetchPreventiveSchedules,
  fetchPreventiveScheduleDates,
  deletePreventiveSchedules,
  postRoadmapScheduleRetry,
} from '../services/roadmapScheduleApi';
import './RoadmapSchedulePage.css';
import '../../board/toolbar/BoardClickUpFilterChip.css';
import '../../board/toolbar/BoardToolbarSearchFilters.css';
import '../../board/board-search-filters/BoardSearchFilters.css';
import '../../equipment-consult/components/EquipmentConsultProductionPeriodPicker.css';

const FILTER_POPOVER_Z = 13050;
const STATUS_ORDER = ['confirmed', 'error', 'missed', 'pending'];

function toggleSelection(prev, value) {
  return prev.includes(value) ? prev.filter((x) => x !== value) : prev.concat(value);
}

function padTechId(id) {
  const s = String(id ?? '').trim();
  if (!s) return '';
  return s.length >= 4 ? s.slice(0, 4) : s.padStart(4, '0');
}

function initialsFromName(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
}

function defaultJobDay() {
  const today = toYmd(new Date());
  return { startDate: today, endDate: today };
}

/** Roteiro: até hoje. Preventivas: libera datas futuras (até +18 meses). */
function getSchedulePageCalendarBounds(tab) {
  const base = getCalendarBounds();
  if (tab !== 'preventivas') return base;
  const max = new Date();
  max.setMonth(max.getMonth() + 18);
  return { minDate: base.minDate, maxDate: toYmd(max) };
}

function statusLabel(st) {
  if (st === 'confirmed') return 'Confirmado';
  if (st === 'error') return 'Erro';
  if (st === 'missed') return 'Não agendado';
  return 'Pendente';
}

function statusDotsFromCounts(counts) {
  return STATUS_ORDER.filter((k) => (counts?.[k] || 0) > 0);
}

function formatDateTime(value) {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) {
    const s = String(value);
    return s.length >= 16 ? s.slice(0, 19).replace('T', ' ') : s;
  }
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatDurationMs(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n < 0) return null;
  if (n < 1000) return `${Math.round(n)} ms`;
  const sec = n / 1000;
  if (sec < 60) return `${sec.toFixed(1)} s`;
  const min = Math.floor(sec / 60);
  const rem = Math.round(sec % 60);
  return `${min} min ${rem} s`;
}

/**
 * Contagem real de OS do job (não usar records_affected bruto: o SQL costuma
 * duplicar por resultset — ex.: 2 OS × 2 UPDATEs = 4).
 */
function uniqueOsFromJob(job) {
  const raw = Array.isArray(job?.osProcessed) ? job.osProcessed : [];
  const set = new Set(
    raw.map((os) => String(os ?? '').trim()).filter(Boolean)
  );
  return [...set];
}

function jobAffectedOsCount(job) {
  const osList = uniqueOsFromJob(job);
  if (osList.length) return osList.length;
  const n = Number(job?.recordsAffected);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function prevStatusLabel(st) {
  if (st === 'done') return 'Gerada';
  if (st === 'error') return 'Erro';
  if (st === 'cancelled') return 'Cancelada';
  return 'Pendente';
}

function formatPrevScheduleDate(ymd) {
  if (!ymd) return '—';
  const s = String(ymd);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const [y, m, d] = s.slice(0, 10).split('-');
    return `${d}/${m}/${y}`;
  }
  return ymdToBr(s) || s;
}

function clientLabelFromPrev(it) {
  const contrato = String(it?.contrato || '').trim();
  const nome = String(it?.cliente_nome || it?.clienteNome || '').trim();
  if (contrato && nome) return `${contrato} · ${nome}`;
  return contrato || nome || '';
}

function RschedPrevCheckbox({ checked, onChange, title, indeterminate = false }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = Boolean(indeterminate);
  }, [indeterminate]);
  return (
    <label
      className="rsched-prev-check-label"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <input
        ref={ref}
        type="checkbox"
        className="rsched-prev-check-native"
        checked={checked}
        onChange={(e) => onChange?.(e.target.checked)}
        onClick={(e) => e.stopPropagation()}
        aria-label={title || 'Selecionar'}
      />
      <span className="rsched-prev-check-box" aria-hidden />
    </label>
  );
}

function groupPreventivesByDate(items) {
  const map = new Map();
  for (const it of items || []) {
    const key = String(it.schedule_date || '').slice(0, 10) || 'sem-data';
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(it);
  }
  return [...map.entries()]
    .sort((a, b) => String(b[0]).localeCompare(String(a[0])))
    .map(([date, rows]) => ({ date, rows }));
}

function divergenceLines(div) {
  if (!div) return [];
  const diffs = Array.isArray(div.diffs) ? div.diffs : [];
  return diffs.map((d) => {
    const field = d.field || '?';
    const exp = d.expected == null ? '—' : String(d.expected);
    const act = d.actual == null ? '—' : String(d.actual);
    return `${field}: esperado "${exp}" → atual "${act}"`;
  });
}

function appendJobOsDetailLines(lines, job) {
  const osList = uniqueOsFromJob(job);
  const count = jobAffectedOsCount(job);
  if (count > 0 || osList.length) {
    lines.push(`Registros afetados: ${count}`);
  }
  if (osList.length) {
    lines.push(`OS processadas: ${osList.join(', ')}`);
  }
}

function buildJobDetailMessages(jobs, kind, orders) {
  const list = Array.isArray(jobs) ? jobs : [];
  const msgs = [];

  if (kind === 'success') {
    const successJobs = list.filter((j) => j.status === 'success' || j.status === 'ok');
    if (!successJobs.length) {
      msgs.push({
        title: 'Sem execução com sucesso',
        lines: ['Não há log de sucesso do job de agendamento para esta data.'],
      });
      return msgs;
    }
    successJobs.forEach((job) => {
      const lines = [];
      const started = formatDateTime(job.startedAt);
      const finished = formatDateTime(job.finishedAt);
      if (started) lines.push(`Início: ${started}`);
      if (finished) lines.push(`Fim: ${finished}`);
      const dur = formatDurationMs(job.durationMs);
      if (dur) lines.push(`Duração: ${dur}`);
      appendJobOsDetailLines(lines, job);
      if (job.reconcile) {
        const r = job.reconcile;
        if (r.reconcileAttempt) {
          lines.push(
            `Reconcile: restore=${r.restored ?? 0}, rerun=${r.recordsAfterRerun ?? 'n/d'}`
          );
        } else if (r.reason) {
          lines.push(`Reconcile: ${r.reason}`);
        }
      }
      if (job.summary) lines.push(String(job.summary));
      msgs.push({
        title: `Job ${ymdToBr(job.jobDay) || job.jobDay || '—'} · sucesso`,
        lines,
      });
    });
    return msgs;
  }

  // error / general failure details
  const errorJobs = list.filter((j) => j.status === 'error' || j.status === 'failed');
  if (errorJobs.length) {
    errorJobs.forEach((job) => {
      const lines = [];
      const started = formatDateTime(job.startedAt);
      const finished = formatDateTime(job.finishedAt);
      if (started) lines.push(`Início: ${started}`);
      if (finished) lines.push(`Fim: ${finished}`);
      lines.push(`Status: ${job.status}`);
      appendJobOsDetailLines(lines, job);
      if (job.errorMessage) lines.push(String(job.errorMessage));
      else lines.push('Sem mensagem de erro no log.');
      if (job.reconcile?.error) lines.push(`Reconcile: ${job.reconcile.error}`);
      msgs.push({
        title: `Job ${ymdToBr(job.jobDay) || job.jobDay || '—'} · erro`,
        lines,
      });
    });
  }

  const orderMsgs = (orders || [])
    .filter((o) => o.scheduleStatus === 'error' || o.scheduleStatus === 'missed')
    .map((o) => ({
      os: o.os,
      text: o.statusMessage || statusLabel(o.scheduleStatus),
    }));
  if (orderMsgs.length) {
    const unique = [];
    const seen = new Set();
    orderMsgs.forEach((m) => {
      const key = `${m.os}|${m.text}`;
      if (seen.has(key)) return;
      seen.add(key);
      unique.push(m);
    });
    msgs.push({
      title: 'Detalhes por OS',
      lines: unique.map((m) => `OS ${m.os}: ${m.text}`),
    });
  }

  if (!msgs.length) {
    msgs.push({
      title: 'Sem erros registrados',
      lines: ['Não há falha do job de agendamento nem OS com erro para esta data.'],
    });
  }
  return msgs;
}

function SummaryDetailChip({
  className,
  count,
  label,
  kind,
  active,
  disabled,
  onToggle,
  anchorRef,
}) {
  return (
    <button
      type="button"
      ref={anchorRef}
      className={[
        'rsched-sum-chip',
        className,
        active ? 'is-open' : '',
        disabled ? 'is-disabled' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-expanded={active}
      aria-label={`Detalhes de ${label}`}
      disabled={disabled}
      onClick={() => {
        if (!disabled) onToggle?.(kind);
      }}
    >
      <strong>{count}</strong> {label}
    </button>
  );
}

function SummaryDetailPopover({ open, kind, anchorRef, messages, onClose, footer = null }) {
  const [pos, setPos] = useState(null);
  const popRef = useRef(null);

  useLayoutEffect(() => {
    if (!open || !anchorRef?.current) {
      setPos(null);
      return undefined;
    }
    const rect = anchorRef.current.getBoundingClientRect();
    const width = Math.min(420, window.innerWidth - 24);
    let left = rect.left;
    if (left + width > window.innerWidth - 12) {
      left = Math.max(12, window.innerWidth - width - 12);
    }
    const top = rect.bottom + 8;
    setPos({ top, left, width });
    return undefined;
  }, [open, anchorRef, kind, messages, footer]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (popRef.current?.contains(e.target)) return;
      if (anchorRef?.current?.contains(e.target)) return;
      onClose?.();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, onClose, anchorRef]);

  if (!open || !pos) return null;

  const title = kind === 'success' ? 'Detalhes de sucesso' : 'Detalhes de erro';

  return createPortal(
    <div
      ref={popRef}
      className={`rsched-sum-popover rsched-sum-popover--${kind || 'error'}`}
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        width: pos.width,
        zIndex: FILTER_POPOVER_Z,
      }}
      role="dialog"
      aria-label={title}
    >
      <div className="rsched-sum-popover-head">
        <span>{title}</span>
        <button type="button" className="rsched-sum-popover-close" onClick={onClose} aria-label="Fechar">
          <IconX size={14} stroke={1.75} />
        </button>
      </div>
      <div className="rsched-sum-popover-body">
        {(messages || []).map((block, idx) => (
          <div key={`${block.title}-${idx}`} className="rsched-sum-popover-block">
            <div className="rsched-sum-popover-block-title">{block.title}</div>
            <ul>
              {(block.lines || []).map((line, i) => (
                <li key={`${idx}-${i}`}>{line}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      {footer ? <div className="rsched-sum-popover-footer">{footer}</div> : null}
    </div>,
    document.body
  );
}

function formatRetryAttempt(attempt, index) {
  const r = attempt?.result || {};
  const when = formatDateTime(attempt?.at) || '—';
  const lines = [];
  lines.push(attempt?.message || r.message || (attempt?.ok ? 'Concluído' : 'Falhou'));
  if (r.automation?.tipoagend != null) {
    lines.push(
      `Modo: ${Number(r.automation.tipoagend) === 2 ? 'sem roteiro' : 'roteirizado'} (tipo ${r.automation.tipoagend})`
    );
  }
  if (r.forecast) lines.push(`Forecast usado: ${ymdToBr(r.forecast) || r.forecast}`);
  const processed = Array.isArray(r.processed) ? r.processed : [];
  const skipped = Array.isArray(r.skipped) ? r.skipped : [];
  if (processed.length) {
    lines.push(`Processadas (${processed.length}): ${processed.map((p) => p.os).join(', ')}`);
  }
  if (skipped.length) {
    lines.push(
      `Ignoradas (${skipped.length}): ${skipped
        .map((s) => `${s.os}${s.reason ? ` — ${s.reason}` : ''}`)
        .join('; ')}`
    );
  }
  if (r.rolledBack) lines.push('Rollback total aplicado — nenhuma alteração mantida.');
  return {
    title: `${index + 1}ª tentativa · ${when}${attempt?.ok ? ' · ok' : ' · falha'}`,
    lines,
    ok: !!attempt?.ok,
  };
}

function TechAvatar({ name, photoUrl }) {
  const initials = initialsFromName(name);
  if (photoUrl) {
    return (
      <span className="rsched-tech-avatar rsched-tech-avatar--photo" aria-hidden>
        <img src={photoUrl} alt="" decoding="async" referrerPolicy="no-referrer" />
      </span>
    );
  }
  return (
    <span className="rsched-tech-avatar" aria-hidden>
      {initials}
    </span>
  );
}

function RoadmapSchedulePage({ currentUser = null }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isOpen = location.pathname === ROADMAP_SCHEDULE_ROUTE;
  const isAdmin = !!(currentUser && (currentUser.type === '1' || currentUser.type === 1));

  const [jobDay, setJobDay] = useState(defaultJobDay);
  const [dateOpen, setDateOpen] = useState(false);
  const [dateRect, setDateRect] = useState(null);
  const dateBtnRef = useRef(null);

  const [mainTab, setMainTab] = useState('preventivas'); // 'roteiro' | 'preventivas'
  const dateBounds = useMemo(() => getSchedulePageCalendarBounds(mainTab), [mainTab]);
  const [prevItems, setPrevItems] = useState([]);
  const [prevErrorDetail, setPrevErrorDetail] = useState(null);
  const [prevSelectedIds, setPrevSelectedIds] = useState(() => new Set());
  const [prevDeleteBusy, setPrevDeleteBusy] = useState(false);
  const [prevMarkedDates, setPrevMarkedDates] = useState(() => new Set());

  const [openFilter, setOpenFilter] = useState(null);
  const [coordsSel, setCoordsSel] = useState([]);
  const [areasSel, setAreasSel] = useState([]);
  const [techsSel, setTechsSel] = useState([]);
  const [clientsSel, setClientsSel] = useState([]);
  const [search, setSearch] = useState('');

  const [team, setTeam] = useState({ coordenadores: [], areas: [], tecnicos: [] });
  const [viewData, setViewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [summaryDetailKind, setSummaryDetailKind] = useState(null); // 'success' | 'error' | null
  const successChipRef = useRef(null);
  const errorChipRef = useRef(null);

  const [retryBusy, setRetryBusy] = useState(false);
  const [retryPhase, setRetryPhase] = useState(null); // null | 'starting' | 'running' | 'done'
  const [retryAttempts, setRetryAttempts] = useState([]);

  const handleClose = useCallback(() => {
    if (location.pathname === ROADMAP_SCHEDULE_ROUTE) {
      navigate('/app');
    }
  }, [location.pathname, navigate]);

  const load = useCallback(async () => {
    if (!isOpen || !jobDay.startDate) return;
    setLoading(true);
    setError(null);
    try {
      if (mainTab === 'preventivas') {
        const items = await fetchPreventiveSchedules({
          startDate: jobDay.startDate,
          endDate: jobDay.endDate || jobDay.startDate,
        });
        setPrevItems(items);
        setViewData(null);
      } else {
        const data = await fetchRoadmapScheduleView({
          startDate: jobDay.startDate,
          endDate: jobDay.endDate || jobDay.startDate,
        });
        setViewData(data);
      }
    } catch (err) {
      setError(err.message || 'Erro ao carregar');
      if (mainTab === 'preventivas') setPrevItems([]);
      else setViewData(null);
    } finally {
      setLoading(false);
    }
  }, [isOpen, jobDay.startDate, jobDay.endDate, mainTab]);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const t = await fetchTeamForScheduleFilters();
        if (!cancelled) setTeam(t);
      } catch {
        /* filtros ficam vazios */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    load();
  }, [isOpen, load]);

  useEffect(() => {
    if (!isOpen || mainTab !== 'preventivas') {
      setPrevMarkedDates(new Set());
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const dates = await fetchPreventiveScheduleDates({
          startDate: dateBounds.minDate,
          endDate: dateBounds.maxDate,
        });
        if (!cancelled) setPrevMarkedDates(new Set(dates || []));
      } catch {
        if (!cancelled) setPrevMarkedDates(new Set());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, mainTab, dateBounds.minDate, dateBounds.maxDate, prevItems]);

  useEffect(() => {
    setPrevSelectedIds(new Set());
  }, [jobDay.startDate, jobDay.endDate, mainTab]);

  useEffect(() => {
    setPrevSelectedIds((prev) => {
      const valid = new Set((prevItems || []).map((it) => it.id));
      const next = new Set([...prev].filter((id) => valid.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [prevItems]);

  useEffect(() => {
    if (!isOpen) {
      setRetryAttempts([]);
      setRetryPhase(null);
      setRetryBusy(false);
      setPrevSelectedIds(new Set());
    }
  }, [isOpen]);

  useEffect(() => {
    setRetryAttempts([]);
    setRetryPhase(null);
  }, [jobDay.startDate, jobDay.endDate]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (summaryDetailKind) {
        setSummaryDetailKind(null);
        return;
      }
      if (prevErrorDetail) {
        setPrevErrorDetail(null);
        return;
      }
      if (dateOpen) {
        setDateOpen(false);
        return;
      }
      if (openFilter) {
        setOpenFilter(null);
        return;
      }
      handleClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, handleClose, dateOpen, openFilter, summaryDetailKind, prevErrorDetail]);

  const techMetaById = useMemo(() => {
    const map = new Map();
    const areasById = new Map((team.areas || []).map((a) => [a.id, a]));
    const coordsById = new Map((team.coordenadores || []).map((c) => [c.id, c]));
    (team.tecnicos || []).forEach((t) => {
      const id = padTechId(t.id);
      const area = t.areaId != null ? areasById.get(t.areaId) : null;
      const coord =
        area && area.coordenadorId != null ? coordsById.get(area.coordenadorId) : null;
      map.set(id, {
        id,
        name: t.nome || t.name || id,
        areaName: area?.nome || area?.name || '—',
        areaId: area?.id ?? null,
        coordName: coord?.nome || coord?.name || '—',
        coordId: coord?.id ?? null,
      });
    });
    return map;
  }, [team]);

  const avatarIds = useMemo(
    () => (viewData?.technicians || []).map((t) => padTechId(t.idTech)).filter(Boolean),
    [viewData]
  );
  const avatarMap = useBoardTechnicianAvatars(avatarIds, { enabled: isOpen });

  const coordOptions = useMemo(() => {
    if (mainTab === 'preventivas') {
      const names = new Set();
      (prevItems || []).forEach((it) => {
        const meta = techMetaById.get(padTechId(it.cod_tec));
        const name = meta?.coordName;
        if (name && name !== '—') names.add(name);
      });
      return [...names].sort();
    }
    const names = new Set();
    (viewData?.technicians || []).forEach((tech) => {
      const meta = techMetaById.get(padTechId(tech.idTech));
      const name = meta?.coordName;
      if (name && name !== '—') names.add(name);
    });
    return [...names].sort();
  }, [mainTab, viewData, techMetaById, prevItems]);

  const areaOptions = useMemo(() => {
    if (mainTab === 'preventivas') {
      const names = new Set();
      (prevItems || []).forEach((it) => {
        const meta = techMetaById.get(padTechId(it.cod_tec));
        const name = meta?.areaName;
        if (name && name !== '—') names.add(name);
      });
      return [...names].sort();
    }
    const names = new Set();
    (viewData?.technicians || []).forEach((tech) => {
      const meta = techMetaById.get(padTechId(tech.idTech));
      const name = meta?.areaName;
      if (name && name !== '—') names.add(name);
    });
    return [...names].sort();
  }, [mainTab, viewData, techMetaById, prevItems]);

  const techOptions = useMemo(() => {
    if (mainTab === 'preventivas') {
      const names = new Set();
      (prevItems || []).forEach((it) => {
        const meta = techMetaById.get(padTechId(it.cod_tec));
        const name = it.tech_name || meta?.name || padTechId(it.cod_tec);
        if (name) names.add(name);
      });
      return [...names].sort();
    }
    const names = new Set();
    (viewData?.technicians || []).forEach((tech) => {
      const meta = techMetaById.get(padTechId(tech.idTech));
      const name = meta?.name || padTechId(tech.idTech);
      if (name) names.add(name);
    });
    return [...names].sort();
  }, [mainTab, viewData, techMetaById, prevItems]);

  const clientOptions = useMemo(() => {
    const names = new Set();
    (prevItems || []).forEach((it) => {
      const label = clientLabelFromPrev(it);
      if (label) names.add(label);
    });
    return [...names].sort();
  }, [prevItems]);

  useEffect(() => {
    setCoordsSel((prev) => {
      const next = prev.filter((x) => coordOptions.includes(x));
      return next.length === prev.length ? prev : next;
    });
  }, [coordOptions]);

  useEffect(() => {
    setAreasSel((prev) => {
      const next = prev.filter((x) => areaOptions.includes(x));
      return next.length === prev.length ? prev : next;
    });
  }, [areaOptions]);

  useEffect(() => {
    setTechsSel((prev) => {
      const next = prev.filter((x) => techOptions.includes(x));
      return next.length === prev.length ? prev : next;
    });
  }, [techOptions]);

  useEffect(() => {
    setClientsSel((prev) => {
      const next = prev.filter((x) => clientOptions.includes(x));
      return next.length === prev.length ? prev : next;
    });
  }, [clientOptions]);

  const openChange = (key) => (open) => {
    if (open) setDateOpen(false);
    setOpenFilter(open ? key : null);
  };

  const dateLabel = jobDay.startDate ? ymdToBr(jobDay.startDate) : '';
  const dateHasSel = Boolean(jobDay.startDate);
  const searchLower = String(search || '').trim().toLowerCase();

  const rows = useMemo(() => {
    const list = viewData?.technicians || [];
    return list
      .map((tech) => {
        const meta = techMetaById.get(padTechId(tech.idTech)) || {
          id: padTechId(tech.idTech),
          name: padTechId(tech.idTech),
          areaName: '—',
          coordName: '—',
        };
        return { ...tech, meta };
      })
      .filter((row) => {
        if (coordsSel.length && !coordsSel.includes(row.meta.coordName)) return false;
        if (areasSel.length && !areasSel.includes(row.meta.areaName)) return false;
        if (techsSel.length && !techsSel.includes(row.meta.name)) return false;
        if (searchLower) {
          const hay = [
            row.meta.name,
            row.meta.areaName,
            row.meta.coordName,
            row.idTech,
            ...(row.orders || []).map((o) => o.os),
          ]
            .join(' ')
            .toLowerCase();
          if (!hay.includes(searchLower)) return false;
        }
        return true;
      });
  }, [viewData, techMetaById, coordsSel, areasSel, techsSel, searchLower]);

  const filteredSummary = useMemo(() => {
    const orders = rows.flatMap((r) => r.orders);
    return {
      technicians: rows.length,
      orders: orders.length,
      confirmed: orders.filter((o) => o.scheduleStatus === 'confirmed').length,
      error: orders.filter((o) => o.scheduleStatus === 'error').length,
      missed: orders.filter((o) => o.scheduleStatus === 'missed').length,
      pending: orders.filter((o) => o.scheduleStatus === 'pending').length,
    };
  }, [rows]);

  const filteredPrevItems = useMemo(() => {
    return (prevItems || []).filter((it) => {
      const meta = techMetaById.get(padTechId(it.cod_tec)) || {};
      const techName = it.tech_name || meta.name || padTechId(it.cod_tec);
      const clientLabel = clientLabelFromPrev(it);
      if (coordsSel.length && !coordsSel.includes(meta.coordName)) return false;
      if (areasSel.length && !areasSel.includes(meta.areaName)) return false;
      if (techsSel.length && !techsSel.includes(techName)) return false;
      if (clientsSel.length && !clientsSel.includes(clientLabel)) return false;
      if (searchLower) {
        const hay = [
          it.serie,
          it.patrimonio,
          it.modelo,
          techName,
          meta.coordName,
          meta.areaName,
          clientLabel,
          it.contrato,
          it.codcli,
          it.os_number,
          it.error_message,
          prevStatusLabel(it.status),
        ]
          .join(' ')
          .toLowerCase();
        if (!hay.includes(searchLower)) return false;
      }
      return true;
    });
  }, [prevItems, techMetaById, coordsSel, areasSel, techsSel, clientsSel, searchLower]);

  const prevGroups = useMemo(() => groupPreventivesByDate(filteredPrevItems), [filteredPrevItems]);
  const prevSummary = useMemo(() => {
    const list = filteredPrevItems || [];
    return {
      total: list.length,
      done: list.filter((i) => i.status === 'done').length,
      error: list.filter((i) => i.status === 'error').length,
      pending: list.filter((i) => i.status === 'pending').length,
    };
  }, [filteredPrevItems]);

  const prevVisibleIds = useMemo(
    () => (filteredPrevItems || []).map((it) => it.id).filter((id) => id != null),
    [filteredPrevItems]
  );
  const prevAllVisibleSelected =
    prevVisibleIds.length > 0 && prevVisibleIds.every((id) => prevSelectedIds.has(id));
  const prevSomeVisibleSelected =
    prevVisibleIds.some((id) => prevSelectedIds.has(id)) && !prevAllVisibleSelected;
  const prevSelectedCount = prevSelectedIds.size;

  const togglePrevRow = useCallback((id, checked) => {
    setPrevSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const togglePrevSelectAllVisible = useCallback(
    (checked) => {
      setPrevSelectedIds((prev) => {
        const next = new Set(prev);
        if (checked) prevVisibleIds.forEach((id) => next.add(id));
        else prevVisibleIds.forEach((id) => next.delete(id));
        return next;
      });
    },
    [prevVisibleIds]
  );

  const handleDeletePrevItems = useCallback(
    async (ids) => {
      const list = (Array.isArray(ids) ? ids : []).filter((id) => id != null);
      if (!list.length || prevDeleteBusy) return;
      const ok = window.confirm(
        list.length === 1
          ? 'Excluir este agendamento preventivo?'
          : `Excluir ${list.length} agendamentos preventivos?`
      );
      if (!ok) return;
      setPrevDeleteBusy(true);
      try {
        await deletePreventiveSchedules(list);
        setPrevSelectedIds((prev) => {
          const next = new Set(prev);
          list.forEach((id) => next.delete(id));
          return next;
        });
        await load();
      } catch (err) {
        setError(err.message || 'Erro ao excluir');
      } finally {
        setPrevDeleteBusy(false);
      }
    },
    [prevDeleteBusy, load]
  );

  const allOrders = useMemo(() => rows.flatMap((r) => r.orders || []), [rows]);

  const successDetailMessages = useMemo(
    () => buildJobDetailMessages(viewData?.jobs, 'success', allOrders),
    [viewData?.jobs, allOrders]
  );
  const errorDetailMessages = useMemo(
    () => buildJobDetailMessages(viewData?.jobs, 'error', allOrders),
    [viewData?.jobs, allOrders]
  );

  const problemOsList = useMemo(() => {
    const list = allOrders
      .filter((o) => o.scheduleStatus === 'error' || o.scheduleStatus === 'missed')
      .map((o) => String(o.os || '').trim())
      .filter(Boolean);
    return [...new Set(list)];
  }, [allOrders]);

  const hasScheduleErrors = useMemo(() => {
    if (filteredSummary.error > 0 || filteredSummary.missed > 0) return true;
    const jobs = viewData?.jobs || [];
    return jobs.some((j) => j.status === 'error' || j.status === 'failed');
  }, [filteredSummary.error, filteredSummary.missed, viewData?.jobs]);

  const canRetry =
    isAdmin && hasScheduleErrors && problemOsList.length > 0 && !retryBusy && !loading;

  const retryAttemptBlocks = useMemo(
    () => retryAttempts.map((a, i) => formatRetryAttempt(a, i)),
    [retryAttempts]
  );

  const handleManualRetry = useCallback(async () => {
    if (!isAdmin || retryBusy || !problemOsList.length || !hasScheduleErrors) return;
    setSummaryDetailKind(null);
    setRetryBusy(true);
    setRetryPhase('starting');
    try {
      setRetryPhase('running');
      const payload = await postRoadmapScheduleRetry({
        osList: problemOsList,
        jobDay: jobDay.startDate,
      });
      setRetryAttempts((prev) => [
        ...prev,
        {
          at: new Date().toISOString(),
          ok: !!payload.ok,
          message: payload.message,
          result: payload.result || {},
        },
      ]);
      setRetryPhase('done');
      await load();
    } catch (err) {
      setRetryAttempts((prev) => [
        ...prev,
        {
          at: new Date().toISOString(),
          ok: false,
          message: err.message || 'Falha no reprocessamento',
          result: { rolledBack: true },
        },
      ]);
      setRetryPhase('done');
    } finally {
      setRetryBusy(false);
    }
  }, [isAdmin, retryBusy, problemOsList, hasScheduleErrors, jobDay.startDate, load]);

  const toggleSummaryDetail = useCallback((kind) => {
    setSummaryDetailKind((prev) => (prev === kind ? null : kind));
    setDateOpen(false);
    setOpenFilter(null);
  }, []);

  const retryFooter =
    isAdmin && hasScheduleErrors && problemOsList.length > 0 ? (
      <button
        type="button"
        className="rsched-retry-btn"
        disabled={!canRetry}
        onClick={handleManualRetry}
      >
        <IconPlayerPlay size={14} stroke={1.75} />
        {retryBusy
          ? 'Reprocessando…'
          : `Reprocessar ${problemOsList.length} OS com problema`}
      </button>
    ) : null;

  if (!isOpen) return null;

  return createPortal(
    <div
      className="rsched-overlay"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        className="rsched-shell"
        role="dialog"
        aria-modal="true"
        aria-label={mainTab === 'preventivas' ? 'Agendamento de preventivas' : 'Agendamento de roteiro'}
      >
        <div className="rsched-root">
          <header className="rsched-header">
            <div className="rsched-header-top">
              <div className="rsched-header-left">
                <h1 className="rsched-title">
                  {mainTab === 'preventivas' ? 'Agendamento de preventivas' : 'Agendamento de roteiro'}
                </h1>
                <div className="rsched-tabs" role="tablist" aria-label="Tipo de agendamento">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={mainTab === 'preventivas'}
                    className={`rsched-tab${mainTab === 'preventivas' ? ' is-active' : ''}`}
                    onClick={() => setMainTab('preventivas')}
                  >
                    <span className="rsched-tab-sep" aria-hidden />
                    Preventivas
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={mainTab === 'roteiro'}
                    className={`rsched-tab${mainTab === 'roteiro' ? ' is-active' : ''}`}
                    onClick={() => setMainTab('roteiro')}
                  >
                    <span className="rsched-tab-sep" aria-hidden />
                    Roteiro técnico
                  </button>
                </div>
              </div>
              <div className="rsched-header-actions">
                <button
                  type="button"
                  className="board-clickup-filter-chip"
                  aria-label="Atualizar"
                  disabled={loading}
                  onClick={load}
                >
                  <span className="board-clickup-filter-chip-icon" aria-hidden="true">
                    <IconRefresh size={13} stroke={1.5} />
                  </span>
                  <span className="board-clickup-filter-chip-text">
                    <span className="board-clickup-filter-chip-label">
                      {loading ? 'Atualizando…' : 'Atualizar'}
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  className="rsched-close-btn"
                  aria-label="Fechar"
                  onClick={handleClose}
                >
                  <IconX size={16} stroke={1.75} />
                </button>
              </div>
            </div>
          </header>

          <div className="rsched-body">
            <div className="rsched-header-filters">
              <div className="filter-group board-toolbar-filter-group">
                <EcClickUpFilterChip
                  anchorRef={dateBtnRef}
                  label="Data"
                  value={dateLabel}
                  icon={IconCalendar}
                  hasSelection={dateHasSel}
                  isOpen={dateOpen}
                  ariaLabel={mainTab === 'preventivas' ? 'Filtrar por data de geração' : 'Filtrar por data do job'}
                  onClick={() => {
                    const rect = dateBtnRef.current?.getBoundingClientRect();
                    setDateRect(rect || null);
                    setOpenFilter(null);
                    setDateOpen((v) => !v);
                  }}
                />
                {dateOpen ? (
                  <PeriodModal
                    anchorRef={dateBtnRef}
                    anchorRect={dateRect}
                    currentPeriod={dateHasSel ? jobDay : null}
                    minDate={dateBounds.minDate}
                    maxDate={dateBounds.maxDate}
                    markedDates={mainTab === 'preventivas' ? prevMarkedDates : null}
                    dayMode
                    singleMonth
                    hideHeader
                    hideActionsBorder
                    useUserFormActions
                    zIndex={FILTER_POPOVER_Z}
                    modalClassName="ec-module-popover rsched-date-popover"
                    onApply={(startDate) => {
                      const d = startDate || jobDay.startDate;
                      setJobDay({ startDate: d, endDate: d });
                      setDateOpen(false);
                    }}
                    onClear={() => {
                      setJobDay(defaultJobDay());
                      setDateOpen(false);
                    }}
                    onClose={() => setDateOpen(false)}
                  />
                ) : null}

                {mainTab === 'roteiro' || mainTab === 'preventivas' ? (
                  <>
                <BoardToolbarCoordenadorClickUpChip
                  options={coordOptions}
                  selected={coordsSel}
                  isOpen={openFilter === 'coordenador'}
                  onOpenChange={openChange('coordenador')}
                  popoverZIndex={FILTER_POPOVER_Z}
                  onToggleItem={(v) => setCoordsSel((prev) => toggleSelection(prev, v))}
                  onToggleAll={(keys, allMarked) => {
                    setCoordsSel((prev) =>
                      allMarked
                        ? prev.filter((x) => !keys.includes(x))
                        : Array.from(new Set([...prev, ...keys]))
                    );
                  }}
                  onClear={() => setCoordsSel([])}
                  onApply={() => setOpenFilter(null)}
                />
                <BoardToolbarAreaClickUpChip
                  options={areaOptions}
                  selected={areasSel}
                  isOpen={openFilter === 'area'}
                  onOpenChange={openChange('area')}
                  popoverZIndex={FILTER_POPOVER_Z}
                  onToggleItem={(v) => setAreasSel((prev) => toggleSelection(prev, v))}
                  onToggleAll={(keys, allMarked) => {
                    setAreasSel((prev) =>
                      allMarked
                        ? prev.filter((x) => !keys.includes(x))
                        : Array.from(new Set([...prev, ...keys]))
                    );
                  }}
                  onClear={() => setAreasSel([])}
                  onApply={() => setOpenFilter(null)}
                />
                <BoardToolbarTecnicosClickUpChip
                  options={techOptions}
                  selected={techsSel}
                  isOpen={openFilter === 'tecnico'}
                  onOpenChange={openChange('tecnico')}
                  popoverZIndex={FILTER_POPOVER_Z}
                  onToggleItem={(v) => setTechsSel((prev) => toggleSelection(prev, v))}
                  onToggleAll={(keys, allMarked) => {
                    setTechsSel((prev) =>
                      allMarked
                        ? prev.filter((x) => !keys.includes(x))
                        : Array.from(new Set([...prev, ...keys]))
                    );
                  }}
                  onClear={() => setTechsSel([])}
                  onApply={() => setOpenFilter(null)}
                />
                {mainTab === 'preventivas' ? (
                  <BoardToolbarClienteClickUpChip
                    options={clientOptions}
                    selected={clientsSel}
                    isOpen={openFilter === 'cliente'}
                    onOpenChange={openChange('cliente')}
                    popoverZIndex={FILTER_POPOVER_Z}
                    onToggleItem={(v) => setClientsSel((prev) => toggleSelection(prev, v))}
                    onToggleAll={(keys, allMarked) => {
                      setClientsSel((prev) =>
                        allMarked
                          ? prev.filter((x) => !keys.includes(x))
                          : Array.from(new Set([...prev, ...keys]))
                      );
                    }}
                    onClear={() => setClientsSel([])}
                    onApply={() => setOpenFilter(null)}
                  />
                ) : null}
                <div className="rsched-search-wrap">
                  <BoardToolbarExpandSearch
                    value={search}
                    onChange={setSearch}
                    forceExpanded
                    placeholder={
                      mainTab === 'preventivas'
                        ? 'Buscar série, cliente, técnico…'
                        : 'Buscar técnico, OS, área…'
                    }
                    ariaLabel={
                      mainTab === 'preventivas'
                        ? 'Buscar nas preventivas agendadas'
                        : 'Buscar na lista de agendamentos'
                    }
                  />
                </div>
                  </>
                ) : null}
              </div>
            </div>

          {mainTab === 'preventivas' ? (
            <>
              <div className="rsched-summary">
                <span>
                  <strong>{prevSummary.total}</strong> agendamentos
                </span>
                <span className="rsched-sum-ok">
                  <strong>{prevSummary.done}</strong> geradas
                </span>
                <span className="rsched-sum-err">
                  <strong>{prevSummary.error}</strong> erro
                </span>
                <span className="rsched-sum-pend">
                  <strong>{prevSummary.pending}</strong> pendentes
                </span>
                {prevSelectedCount > 0 ? (
                  <button
                    type="button"
                    className="rsched-prev-bulk-del"
                    disabled={prevDeleteBusy}
                    onClick={() => handleDeletePrevItems([...prevSelectedIds])}
                  >
                    <IconTrash size={14} stroke={1.75} aria-hidden />
                    {prevDeleteBusy ? 'Excluindo…' : `Excluir ${prevSelectedCount}`}
                  </button>
                ) : null}
              </div>

              {error ? <div className="rsched-error">{error}</div> : null}

              {!loading && !error && prevGroups.length === 0 ? (
                <div className="rsched-empty">
                  <div className="rsched-empty-placeholder">
                    <IconCalendar className="rsched-empty-icon" size={28} stroke={1.5} />
                    <h3 className="rsched-empty-title">Nenhuma preventiva programada</h3>
                    <p className="rsched-empty-desc">
                      Não há preventivas agendadas no período selecionado.
                    </p>
                  </div>
                </div>
              ) : null}

              {prevGroups.length > 0 ? (
                <div className="rsched-prev-list">
                  {prevGroups.map((g) => {
                    const groupIds = g.rows.map((r) => r.id).filter((id) => id != null);
                    const groupAllSelected =
                      groupIds.length > 0 && groupIds.every((id) => prevSelectedIds.has(id));
                    const groupSomeSelected =
                      groupIds.some((id) => prevSelectedIds.has(id)) && !groupAllSelected;
                    return (
                    <section key={g.date} className="rsched-prev-group">
                      <header className="rsched-prev-group-head">
                        <RschedPrevCheckbox
                          checked={groupAllSelected}
                          indeterminate={groupSomeSelected}
                          title="Selecionar grupo"
                          onChange={(checked) => {
                            setPrevSelectedIds((prev) => {
                              const next = new Set(prev);
                              if (checked) groupIds.forEach((id) => next.add(id));
                              else groupIds.forEach((id) => next.delete(id));
                              return next;
                            });
                          }}
                        />
                        <span className="rsched-prev-group-title">
                          Geração em {formatPrevScheduleDate(g.date)}
                        </span>
                        <span className="rsched-prev-group-count">{g.rows.length}</span>
                      </header>
                      <div className="rsched-prev-table-wrap">
                        <table className="rsched-prev-table">
                          <thead>
                            <tr>
                              <th className="rsched-prev-th-check">
                                <RschedPrevCheckbox
                                  checked={prevAllVisibleSelected}
                                  indeterminate={prevSomeVisibleSelected}
                                  title="Selecionar todos"
                                  onChange={togglePrevSelectAllVisible}
                                />
                              </th>
                              <th>Série</th>
                              <th>Patrimônio</th>
                              <th>Modelo</th>
                              <th>Técnico</th>
                              <th>Status</th>
                              <th>OS</th>
                              <th>Detalhe</th>
                              <th className="rsched-prev-th-actions" />
                            </tr>
                          </thead>
                          <tbody>
                            {g.rows.map((row) => (
                              <tr key={row.id} className={`rsched-prev-row is-${row.status}${prevSelectedIds.has(row.id) ? ' is-selected' : ''}`}>
                                <td className="rsched-prev-td-check">
                                  <RschedPrevCheckbox
                                    checked={prevSelectedIds.has(row.id)}
                                    title="Selecionar"
                                    onChange={(checked) => togglePrevRow(row.id, checked)}
                                  />
                                </td>
                                <td className="rsched-prev-mono">{row.serie}</td>
                                <td className="rsched-prev-mono">{row.patrimonio || '—'}</td>
                                <td>{row.modelo || '—'}</td>
                                <td>{row.tech_name || row.cod_tec || '—'}</td>
                                <td>
                                  <span className={`rsched-prev-status is-${row.status}`}>
                                    {prevStatusLabel(row.status)}
                                  </span>
                                </td>
                                <td className="rsched-prev-mono">{row.os_number || '—'}</td>
                                <td>
                                  {row.status === 'error' ? (
                                    <button
                                      type="button"
                                      className="rsched-prev-err-btn"
                                      title={row.error_message || 'Ver erro'}
                                      onClick={() => setPrevErrorDetail(row)}
                                    >
                                      Ver erro
                                    </button>
                                  ) : row.status === 'done' ? (
                                    <span className="rsched-prev-ok-hint">OK</span>
                                  ) : (
                                    <span className="rsched-prev-pend-hint">Aguardando</span>
                                  )}
                                </td>
                                <td className="rsched-prev-td-actions">
                                  <button
                                    type="button"
                                    className="rsched-prev-row-del"
                                    title="Excluir agendamento"
                                    disabled={prevDeleteBusy}
                                    onClick={() => handleDeletePrevItems([row.id])}
                                  >
                                    <IconTrash size={14} stroke={1.7} aria-hidden />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </section>
                    );
                  })}
                </div>
              ) : null}

              {prevErrorDetail ? (
                <div
                  className="rsched-prev-err-overlay"
                  role="presentation"
                  onMouseDown={(e) => {
                    if (e.target === e.currentTarget) setPrevErrorDetail(null);
                  }}
                >
                  <div
                    className="rsched-prev-err-modal"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Detalhe do erro"
                  >
                    <div className="rsched-prev-err-modal-head">
                      <strong>Erro na geração</strong>
                      <button
                        type="button"
                        className="rsched-close-btn"
                        aria-label="Fechar"
                        onClick={() => setPrevErrorDetail(null)}
                      >
                        <IconX size={16} stroke={1.75} />
                      </button>
                    </div>
                    <div className="rsched-prev-err-modal-body">
                      <p className="rsched-prev-err-serie">
                        Série <span className="rsched-prev-mono">{prevErrorDetail.serie}</span>
                        {prevErrorDetail.patrimonio
                          ? ` · Pat. ${prevErrorDetail.patrimonio}`
                          : ''}
                      </p>
                      <p className="rsched-prev-err-msg">
                        {prevErrorDetail.error_message || 'Falha sem mensagem detalhada.'}
                      </p>
                      {divergenceLines(prevErrorDetail.divergence).length > 0 ? (
                        <div className="rsched-prev-err-div">
                          <div className="rsched-prev-err-div-title">Divergências</div>
                          <ul>
                            {divergenceLines(prevErrorDetail.divergence).map((line, idx) => (
                              <li key={idx}>{line}</li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            <>
          <div className="rsched-summary">
            <span>
              <strong>{filteredSummary.technicians}</strong> técnicos
            </span>
            <span>
              <strong>{filteredSummary.orders}</strong> OS
            </span>
            <SummaryDetailChip
              className="rsched-sum-ok"
              count={filteredSummary.confirmed}
              label="confirmadas"
              kind="success"
              active={summaryDetailKind === 'success'}
              disabled={false}
              anchorRef={successChipRef}
              onToggle={toggleSummaryDetail}
            />
            <SummaryDetailChip
              className="rsched-sum-err"
              count={filteredSummary.error}
              label="erro"
              kind="error"
              active={summaryDetailKind === 'error'}
              disabled={false}
              anchorRef={errorChipRef}
              onToggle={toggleSummaryDetail}
            />
            <span className="rsched-sum-miss">
              <strong>{filteredSummary.missed}</strong> não agendadas
            </span>
            <span className="rsched-sum-pend">
              <strong>{filteredSummary.pending}</strong> pendentes
            </span>
          </div>

          <SummaryDetailPopover
            open={summaryDetailKind === 'success'}
            kind="success"
            anchorRef={successChipRef}
            messages={successDetailMessages}
            onClose={() => setSummaryDetailKind(null)}
          />
          <SummaryDetailPopover
            open={summaryDetailKind === 'error'}
            kind="error"
            anchorRef={errorChipRef}
            messages={errorDetailMessages}
            onClose={() => setSummaryDetailKind(null)}
            footer={retryFooter}
          />

          {retryBusy || retryPhase ? (
            <div
              className={`rsched-retry-progress${retryBusy ? ' is-running' : ''}${
                retryPhase === 'done' && !retryBusy ? ' is-done' : ''
              }`}
              role="status"
              aria-live="polite"
            >
              {retryBusy ? (
                <>
                  <span className="rsched-retry-spinner" aria-hidden />
                  <span>
                    {retryPhase === 'starting'
                      ? 'Iniciando reprocessamento…'
                      : `Processando ${problemOsList.length} OS com problema (rollback total se falhar)…`}
                  </span>
                </>
              ) : (
                <span>Reprocessamento finalizado — status atual atualizado abaixo.</span>
              )}
            </div>
          ) : null}

          {retryAttemptBlocks.length > 0 ? (
            <div className="rsched-retry-history" aria-label="Histórico de tentativas">
              <div className="rsched-retry-history-head">
                Resultados das tentativas
                <span className="rsched-retry-history-hint">
                  Status geral atual (chips acima) se sobrepõe ao histórico
                </span>
              </div>
              {retryAttemptBlocks.map((block, idx) => (
                <div
                  key={`retry-${idx}`}
                  className={`rsched-retry-history-block${block.ok ? ' is-ok' : ' is-fail'}`}
                >
                  <div className="rsched-retry-history-title">{block.title}</div>
                  <ul>
                    {(block.lines || []).map((line, i) => (
                      <li key={`retry-${idx}-${i}`}>{line}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : null}

          {error ? <div className="rsched-error">{error}</div> : null}

          {!loading && !error && rows.length === 0 ? (
            <div className="rsched-empty">
              <div className="rsched-empty-placeholder">
                <IconCalendar
                  size={48}
                  stroke={1.25}
                  className="rsched-empty-icon"
                  aria-hidden
                />
                <h3 className="rsched-empty-title">Nenhum agendamento</h3>
                <p className="rsched-empty-desc">
                  Não há técnicos com agendamento na data selecionada
                  {coordsSel.length || areasSel.length || techsSel.length || search
                    ? ', ou nenhum resultado para os filtros aplicados'
                    : ''}
                  . Altere a data ou limpe os filtros para tentar novamente.
                </p>
              </div>
            </div>
          ) : null}

          {rows.length > 0 ? (
          <div className="rsched-grid">
            {rows.map((row) => {
              const isExpanded = !!expanded[row.idTech];
              const name = row.meta.name;
              const photoUrl = avatarMap.get(padTechId(row.idTech)) || null;
              const dots = statusDotsFromCounts(row.counts);
              const osTotal = row.orders.length;
              return (
                <div
                  key={row.idTech}
                  className={`rsched-tech-wrap${isExpanded ? ' is-expanded' : ' is-collapsed'}`}
                >
                  <button
                    type="button"
                    className="rsched-tech-collapsed"
                    aria-expanded={isExpanded}
                    aria-label={isExpanded ? `Recolher ${name}` : `Expandir ${name}`}
                    onClick={() =>
                      setExpanded((s) => ({ ...s, [row.idTech]: !s[row.idTech] }))
                    }
                  >
                    <TechAvatar name={name} photoUrl={photoUrl} />
                    <span className="rsched-tech-name-row">
                      <span className="rsched-tech-name">{name}</span>
                      <span className="rsched-tech-os-count" title={`${osTotal} OS`}>
                        {osTotal}
                      </span>
                    </span>
                    <span className="rsched-tech-meta">
                      <span className="rsched-tech-area">{row.meta.areaName}</span>
                      <span className="rsched-status-dots" aria-hidden>
                        {dots.map((st) => (
                          <span
                            key={st}
                            className={`rsched-status-dot rsched-status-dot--${st}`}
                            title={statusLabel(st)}
                          />
                        ))}
                      </span>
                    </span>
                    {isExpanded ? (
                      <span
                        className="rsched-tech-minimize"
                        role="presentation"
                        title="Recolher"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpanded((s) => ({ ...s, [row.idTech]: false }));
                        }}
                      >
                        <IconChevronUp size={14} stroke={1.75} />
                      </span>
                    ) : null}
                  </button>

                  {isExpanded ? (
                    <div className="rsched-tech-body">
                      <div className="rsched-tech-body-head">
                        <span>{row.meta.coordName}</span>
                      </div>
                      <div className="rsched-os-chips">
                        {row.orders.map((o) => (
                          <span
                            key={`${o.os}-${o.forecast}`}
                            className={`rsched-os-chip rsched-os-chip--${o.scheduleStatus}`}
                            title={[
                              `OS ${o.os}`,
                              `#${o.sequence}`,
                              `Forecast ${ymdToBr(o.forecast)}`,
                              `Job ${ymdToBr(o.jobDay)}`,
                              statusLabel(o.scheduleStatus),
                              o.statusMessage || '',
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          >
                            <span className="rsched-os-seq">{o.sequence}</span>
                            <span className="rsched-os-code">{o.os}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
          ) : null}
            </>
          )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default RoadmapSchedulePage;
